import type { Bi } from '@/sanskrit/lib/sanskrit/types'

export type SourceId =
    | 'panini'
    | 'dhatupatha'
    | 'lsk'
    | 'kale'
    | 'macdonell'
    | 'whitney'
    | 'apte-guide'
    | 'mw'
    | 'apte-dict'
    | 'kashika'
    | 'vani'

export interface ContentSource {
    id: SourceId
    title: string
    author: string
    year?: string
    note: Bi
}

/**
 * Reference works the seeded content was cross-checked against.
 * These are cited at the level of the work; page or section numbers are only given where certain.
 */
export const SOURCES: Record<SourceId, ContentSource> = {
    panini: {
        id: 'panini',
        title: 'अष्टाध्यायी (Aṣṭādhyāyī)',
        author: 'पाणिनि (Pāṇini)',
        note: {
            hi: 'संस्कृत व्याकरण का मूल सूत्रग्रन्थ। सूत्र-संख्याएँ अध्याय.पाद.सूत्र क्रम में दी गई हैं।',
            en: 'The foundational grammar of Sanskrit. Sūtra numbers are given as chapter.quarter.sūtra.'
        }
    },
    dhatupatha: {
        id: 'dhatupatha',
        title: 'धातुपाठ (Dhātupāṭha)',
        author: 'पाणिनीय परम्परा',
        note: {
            hi: 'धातुओं की सूची, उनके गण, पद और अर्थ सहित।',
            en: 'The traditional list of verbal roots with their class, pada and meaning.'
        }
    },
    lsk: {
        id: 'lsk',
        title: 'लघुसिद्धान्तकौमुदी (Laghusiddhāntakaumudī)',
        author: 'वरदराज (Varadarāja)',
        note: {
            hi: 'शब्दरूप और धातुरूप की पारम्परिक प्रक्रिया का प्रसिद्ध पाठ्यग्रन्थ।',
            en: 'Classic teaching text for traditional derivations of noun and verb forms.'
        }
    },
    kale: {
        id: 'kale',
        title: 'A Higher Sanskrit Grammar',
        author: 'M. R. Kale',
        year: '1894',
        note: {
            hi: 'अंग्रेज़ी में विस्तृत संस्कृत व्याकरण, रूप-तालिकाओं सहित।',
            en: 'Detailed English grammar of Classical Sanskrit with full paradigms.'
        }
    },
    macdonell: {
        id: 'macdonell',
        title: 'A Sanskrit Grammar for Students',
        author: 'A. A. Macdonell',
        year: '1927',
        note: {
            hi: 'विद्यार्थियों के लिए संक्षिप्त व्याकरण।',
            en: 'Concise student grammar with declension and conjugation tables.'
        }
    },
    whitney: {
        id: 'whitney',
        title: 'Sanskrit Grammar',
        author: 'W. D. Whitney',
        year: '1889',
        note: {
            hi: 'ऐतिहासिक दृष्टि से लिखा गया व्याकरण।',
            en: 'Descriptive grammar covering Classical and Vedic forms.'
        }
    },
    'apte-guide': {
        id: 'apte-guide',
        title: 'The Student’s Guide to Sanskrit Composition',
        author: 'V. S. Apte',
        note: {
            hi: 'वाक्य-रचना और कारक-प्रयोग के लिए।',
            en: 'Syntax, case usage and composition.'
        }
    },
    mw: {
        id: 'mw',
        title: 'A Sanskrit-English Dictionary',
        author: 'M. Monier-Williams',
        year: '1899',
        note: {
            hi: 'शब्दों के अर्थ और लिङ्ग के लिए।',
            en: 'Word meanings and genders.'
        }
    },
    'apte-dict': {
        id: 'apte-dict',
        title: 'The Practical Sanskrit-English Dictionary',
        author: 'V. S. Apte',
        note: {
            hi: 'शब्दों के अर्थ और प्रयोग के लिए।',
            en: 'Word meanings and usage.'
        }
    },
    kashika: {
        id: 'kashika',
        title: 'काशिकावृत्ति (Kāśikāvṛtti)',
        author: 'जयादित्य और वामन',
        note: {
            hi: 'अष्टाध्यायी पर वृत्ति, पारम्परिक उदाहरणों सहित।',
            en: 'Commentary on the Aṣṭādhyāyī with the traditional examples.'
        }
    },
    vani: {
        id: 'vani',
        title: 'Suvaani content team',
        author: 'Suvaani',
        note: {
            hi: 'आधुनिक दैनिक-प्रयोग के शब्द और पाठ्य-वाक्य, जिनकी समीक्षा अभी विद्वान् से करानी है।',
            en: 'Modern everyday words and teaching sentences, pending sign-off by a Sanskrit scholar.'
        }
    }
}
